import React, { useEffect, useState } from 'react';
import AlertMessage from '../../AlertMessage';
import FieldLevelHelp from '../../components/FieldLevelHelp';
import XcomID from '../../components/XcomID';
import PanelDetails from '../../panel/PanelDetails';
import { getInvoiceBalanceDue, logEvent } from '../../util/apiUtils';
import { isDomesticRequisitionType } from '../../util/gearOrderUtil';
import ActivationNumber from './ActivationNumber';
import BillingInformation from './BillingInformation';
import RequisitionStatus, {isResultsReady} from './RequisitionStatus';
import SampleCollectionInformation from './SampleCollectionInformation';
import SaveCptCodeModal from './SaveCptCodeModal';
import DxCodesInformation from "./DxCodesInformation";

export default function RequisitionDetails({
	requisition,
	canEditAny,
	gearOrder,
	omsRequisition,
	editingSection,
	editingReq,
	lastSavedReq,
	lastSavedSection,
	index,
	userInfo,
	price,
	cancelEdit,
	saveEdit,
	isEditSaveInProgress,
	editRef,
	inlineSaveError,
	getWorkflowStep,
	editSection,
	confirmed,
	recommendedProductOrderableItemIds,
	showRequisition,
	removeClickHandler,
	hasMixedPricingReqs,
	medicalNecessityRequired,
	testSelectionsMissing,
   doNotQuotePrices
}) {
	const [showCptModal, setShowCptModal] = useState(false);
	const [invoiceBalanceDue, setInvoiceBalanceDue] = useState(null);

	const accessionNo = requisition.accessionNo;
	const customerType = userInfo.type;
	const domesticRequisitionType = isDomesticRequisitionType(gearOrder);

	/**
	 * Fetch the order's balance due if an invoice payment is appropriate for the order and we have
	 * all of the data needed to validate an order for payment.
	 */
	const hasRequiredPaymentData = !!(
		accessionNo &&
		requisition.id &&
		requisition.reqNumber &&
		gearOrder.patientInfo?.dateOfBirth &&
		domesticRequisitionType &&
		customerType === 'prc' &&
		!gearOrder.physicianInTheLoop &&
		isResultsReady(gearOrder, requisition)
	);
	useEffect(() => {
		if (hasRequiredPaymentData) {
			getInvoiceBalanceDue(accessionNo)
				.then((balance) => {
					setInvoiceBalanceDue(Number(balance) || 0);
				})
				.catch((err) => {
					// Failed to fetch balance. Balance and Pay Bill button will not be displayed.
				});
		} else {
			setInvoiceBalanceDue(null);
		}
	}, [accessionNo, hasRequiredPaymentData]);

	const toggleCptModal = () => {
		setShowCptModal((prevVal) => !prevVal);

		logEvent(
			'{} - "View CPT Codes" clicked. gearOrderId={}, gearReqId={}, omsRequisitionId={}',
			'UI_ACTION_VIEW_CPT_CODES',
			gearOrder.id, requisition.id, omsRequisition?.id
		);
	}

	const canEditReq =
		canEditAny && (userInfo.adminUser === 'true' || !requisition.accessionNo) && !gearOrder.physicianInTheLoop;

	// HD-69589: Lock down panel edits by a patient if the practitioner selected the panels or has confirmed the selections
	// SCR-7673: Prevent test selections for paperless requisitions
	const practitionerProvidedPanels =
		gearOrder?.workflow?.practitionerConfirmed ||
		gearOrder?.workflow?.steps?.find((step) => step.id === 2)?.userType === 'myGDX' ||
		omsRequisition?.paperless;

	let insuranceRequiredForPayment =
		gearOrder.medicareBillingNoInsurance &&
		requisition.medicareTricareBillingSelected &&
		price &&
		price.medicarePrice !== price.tricarePrice;

	let priceMarkup = null;
	let displayPriceLabel = false;
	if (accessionNo) {
		// Don't show the requisition price for accessioned orders
	} else if (requisition.physicianBillingSelected) {
		priceMarkup = (
			<FieldLevelHelp
				textClassName="text-info"
				iconClassName="fa-info-circle"
				helpText="Physician account billing selected."
				large="true"
			/>
		);
	} else if (price && price.error) {
		priceMarkup = (
			<FieldLevelHelp
				textClassName="text-warning"
				iconClassName="fa-exclamation-triangle"
				helpText="Unable to determine price."
				large="true"
			/>
		);
	} else if (price && price.placeholder) {
		priceMarkup = <i className="fa fa-spinner fa-spin fa-fw"></i>;
	} else if (gearOrder.pitlRecollectionOrderSummary) {
		priceMarkup = null;
	} else if (insuranceRequiredForPayment) {
		priceMarkup = (
			<FieldLevelHelp
				textClassName="text-warning"
				iconClassName="fa-exclamation-triangle"
				helpText="Price cannot be determined until the Insurance step is completed."
				large="true"
			/>
		);
	} else if (price) {
		priceMarkup = '$' + parseFloat(Math.round(price.price * 100) / 100).toFixed(2);
		displayPriceLabel = true;
	}

	const showPanelDetails = !testSelectionsMissing &&
		 (requisition.panel || requisition.components || requisition.addons) &&
		 !(hasMixedPricingReqs && medicalNecessityRequired) &&
		 !gearOrder.physicianInTheLoop &&
		 (userInfo.type !== 'prc' || !practitionerProvidedPanels);
	const showRequisitionEditButton = canEditReq && (userInfo.type !== 'prc' || !practitionerProvidedPanels);
	// PRC users can only "delete" when the order contains a single requisition or if the order is Physician in the Loop
	// SCR-7993: Requisition cannot be deleted if both patient and practitioner have confirmed the order
	const showRequisitionDeleteButton =
		showRequisitionEditButton &&
		(userInfo.type !== 'prc' || gearOrder.requisitions?.length === 1) &&
		(!gearOrder.workflow?.practitionerConfirmed || !gearOrder.workflow?.patientConfirmed);
	const showRequisitionCancelOrderButton =
		canEditAny &&
		requisition.physicianInTheLoop &&
		!requisition.accessionNo &&
		!confirmed &&
		!gearOrder.pitlRecollectionOrderSummary;
	// Show "Save for later" for admin or prc users when the order contains multiple requisitions
	const showRequisitionSaveForLaterButton =
		canEditAny &&
		gearOrder.requisitions?.length > 1 &&
		(userInfo.type === 'prc' || userInfo.adminUser === 'true') &&
		!showRequisitionCancelOrderButton &&
		!(requisition.physicianInTheLoop && requisition.accessionNo) && // Don't show for accessioned PITL
		!gearOrder.pitlRecollectionOrderSummary;

	const isRecommendedProduct = recommendedProductOrderableItemIds?.includes(omsRequisition?.orderableItem?.id);

	const reqCount = gearOrder.requisitions?.length;

	return (
		<div
			className={'confirmation-section' + (isRecommendedProduct ? ' recommended-product' : '')}
			key={requisition.id}
		>
			{requisition.xComId && !requisition.physicianBillingSelected && priceMarkup && (
				<div className="row">
					<div className="col-sm-8 col-sm-offset-4 col-md-6 col-md-offset-6 col-lg-5 col-lg-offset-7 text-right small text-info">
						<span>
							<i className="fa fa-info-circle padding-x-xs"></i>If you provided the full initial payment
							with your original specimen submission, you do not need to submit an additional payment.
						</span>
					</div>
				</div>
			)}
			<div>
				<h3 className="confirmation-well-header row">
					<div className="col-xs-8">
						{requisition.omsOrderableItemName}
						{(omsRequisition && !gearOrder.physicianInTheLoop) && (
							<span className="pl-20">
								<button
									className="btn-link btn-unstyled"
									style={{fontSize: '65%', textDecoration: 'underline'}}
									onClick={toggleCptModal}
									title="View Test Selection and CPT Code Details"
								>
									View Details
								</button>
							</span>
						)}
						<ActivationNumber omsRequisition={omsRequisition} />
						{requisition.xComId && (
							<div className="small">
								<XcomID value={requisition.xComId} />
							</div>
						)}
						<div className="separator-2"></div>
					</div>
					<div className="col-xs-4 text-right">
						{!!(priceMarkup && !invoiceBalanceDue) && (
							<>
								{displayPriceLabel && <h4 className="mt-0 mb-0">Amount Due Today</h4>}
								<span style={{ display: 'inline-block', marginBottom: '0.2em' }}>{priceMarkup}</span>
								<br />
							</>
						)}
						{!!invoiceBalanceDue && (
							<>
								<h4 className="mt-0 mb-0">Amount Due Today</h4>
								<span>{'$' + parseFloat(Math.round(invoiceBalanceDue * 100) / 100).toFixed(2)}</span>
							</>
						)}
						<>
							{showRequisitionEditButton && (
								<button
									type="button"
									title="edit"
									className="btn btn-edit btn-primary"
									onClick={() => showRequisition(requisition.id, 'panel')}
								>
									Edit
								</button>
							)}
							{showRequisitionDeleteButton && (
								<button
									type="button"
									title="remove"
									className="btn btn-edit btn-danger"
									data-gearreqid={requisition.id}
									onClick={removeClickHandler}
								>
									Delete
								</button>
							)}
							{showRequisitionCancelOrderButton && (
								<button
									type="button"
									title="remove"
									className="btn btn-edit btn-danger"
									data-gearreqid={requisition.id}
									onClick={removeClickHandler}
								>
									{reqCount > 1 ? 'Delete' : 'Cancel Order'}
								</button>
							)}
						</>
					</div>
				</h3>
			</div>
			{hasMixedPricingReqs && medicalNecessityRequired && (
				<div data-step="medicare">
					<h4>Test Information</h4>
					<AlertMessage type="danger">
						<strong>
							This order is being placed for a Medicare or Tricare patient but Medical Necessity has not
							been confirmed.
							<br />
							Please{' '}
							<button
								type="button"
								className="btn-link btn-unstyled"
								onClick={() => showRequisition(requisition.id, 'panel')}
							>
								click here
							</button>{' '}
							to return to the test selections page to confirm your selections.
						</strong>
					</AlertMessage>
				</div>
			)}

			{showCptModal && (
				<SaveCptCodeModal
					gearOrderId={gearOrder.id}
					gearRequisition={requisition}
					omsRequisition={omsRequisition}
					onClose={() => setShowCptModal(false)}
				/>
			)}

			<RequisitionStatus
				gearOrder={gearOrder}
				requisition={requisition}
				omsRequisition={omsRequisition}
				canEditReq={canEditReq}
				userType={userInfo.type}
				showRequisition={showRequisition}
				editingReq={editingReq}
				editingSection={editingSection}
				editSection={editSection}
				invoiceBalanceDue={invoiceBalanceDue}
			/>

			{testSelectionsMissing && (
				<>
					<h4>Test Information</h4>
					<AlertMessage type="danger" additionalCssClass="mt-0">
						<strong>
							Test selections are required. Click{' '}
							<button
								type="button"
								className="btn-link btn-unstyled"
								onClick={() => showRequisition(requisition.id, 'panel')}
							>
								here
							</button>{' '}
							to add or confirm your selections.
						</strong>
					</AlertMessage>
				</>
			)}

			{showPanelDetails && (
					<PanelDetails
						reqResponses={requisition}
						requsitionStructure={omsRequisition}
						key={'panel_details_' + index}
						gearOrderId={gearOrder.id}
						showRequisition={showRequisition}
						userInfo={userInfo}
						editDiagnosis={
							canEditReq &&
							customerType !== 'prc' &&
							domesticRequisitionType &&
							(() => showRequisition(requisition.id, 'diagnosis'))
						}
						canEditDiagnosis={
							 canEditReq &&
							 customerType !== 'prc' &&
							 domesticRequisitionType
						}
						gearOrder={gearOrder}
						requisition={requisition}
						omsRequisition={omsRequisition}
						editingReq={editingReq}
						editingSection={editingSection}
						editRef={editRef}
						cancelEdit={cancelEdit}
						saveEdit={saveEdit}
						isEditSaveInProgress={isEditSaveInProgress}
						inlineSaveError={inlineSaveError}
						getWorkflowStep={getWorkflowStep}
						editSection={editSection}
						lastSavedReq={lastSavedReq}
						lastSavedSection={lastSavedSection}
					/>
				)}

			{(!requisition.accessionNo || userInfo.type === 'myGDX') && (
				<>
					<BillingInformation
						gearOrder={gearOrder}
						requisition={requisition}
						omsRequisition={omsRequisition}
						userInfo={userInfo}
						editingReq={editingReq}
						editingSection={editingSection}
						editRef={editRef}
						cancelEdit={cancelEdit}
						saveEdit={saveEdit}
						isEditSaveInProgress={isEditSaveInProgress}
						inlineSaveError={inlineSaveError}
						getWorkflowStep={getWorkflowStep}
						canEditReq={canEditReq}
						editSection={editSection}
						lastSavedReq={lastSavedReq}
						lastSavedSection={lastSavedSection}
						doNotQuotePrices={doNotQuotePrices}
					/>
					<SampleCollectionInformation
						gearOrder={gearOrder}
						requisition={requisition}
						omsRequisition={omsRequisition}
						userType={customerType}
						editingReq={editingReq}
						editingSection={editingSection}
						cancelEdit={cancelEdit}
						saveEdit={saveEdit}
						isEditSaveInProgress={isEditSaveInProgress}
						editRef={editRef}
						inlineSaveError={inlineSaveError}
						getWorkflowStep={getWorkflowStep}
						canEditReq={canEditReq}
						editSection={editSection}
						lastSavedReq={lastSavedReq}
						lastSavedSection={lastSavedSection}
					/>
				</>
			)}
			{!showPanelDetails &&
				 <DxCodesInformation
					  gearOrder={gearOrder}
					  requisition={requisition}
					  omsRequisition={omsRequisition}
					  userInfo={userInfo}
					  editingReq={editingReq}
					  editingSection={editingSection}
					  editRef={editRef}
					  cancelEdit={cancelEdit}
					  saveEdit={saveEdit}
					  isEditSaveInProgress={isEditSaveInProgress}
					  inlineSaveError={inlineSaveError}
					  getWorkflowStep={getWorkflowStep}
					  editSection={editSection}
					  lastSavedReq={lastSavedReq}
					  lastSavedSection={lastSavedSection}
					  canEditDiagnosis={false}
				 />
			}
			{showRequisitionSaveForLaterButton && (
				<button
					type="button"
					title="Move this requisition to a new order"
					className="btn btn-link btn-unstyled"
					data-gearreqid={requisition.id}
					data-saveforlater={true}
					onClick={removeClickHandler}
				>
					Save for later
				</button>
			)}
		</div>
	);
}